// run-command-session-deps.ts
//
// Builds the REAL `SessionRunnerDeps` for `avatar autopilot run` (P9b): the
// production seam that was missing — session-runner.ts always works fine in
// isolation and in E2E (fake worker), but nothing outside tests ever built
// `deps.spawn`/`now`/`sleep` for a live `claude -p` invocation. This module
// is that "production spawn" — a thin wrapper over `node:child_process.spawn`
// that:
//   1. Resolves the `claude` binary from PATH by default (spawn's own
//      resolution when the command has no path separator — nothing to do).
//   2. Substitutes a different binary when `AVATAR_AUTOPILOT_CLAUDE_BIN` is
//      set — test/E2E only (never a silent production behavior; callers
//      MUST surface `isClaudeBinOverridden()` in doctor/status output so an
//      operator never mistakes a test binary for the real one).
import { spawn as nodeSpawn } from "node:child_process";
import { wrapSpawnRecordingActiveWorker } from "./active-worker-record.js";
import type { SpawnFn } from "./claude-process-group-spawner.js";
import type { SessionRunnerDeps } from "./session-runner.js";

const CLAUDE_BIN_OVERRIDE_ENV = "AVATAR_AUTOPILOT_CLAUDE_BIN";

/** True when the claude binary is overridden for this process — surface this, never hide it (brief requirement). */
export function isClaudeBinOverridden(env: NodeJS.ProcessEnv = process.env): boolean {
  return Boolean(env[CLAUDE_BIN_OVERRIDE_ENV]);
}

/**
 * The binary the worker spawn runs: the override when set, else `claude` from
 * PATH — ONE resolution shared with doctor's probes (review 06g M3: doctor
 * used to probe PATH while the worker ran the override).
 */
export function resolveClaudeBin(env: NodeJS.ProcessEnv = process.env): string {
  return env[CLAUDE_BIN_OVERRIDE_ENV] || "claude";
}

/** Human-readable line for doctor/run/status output when the override is active. */
export function claudeBinOverrideNotice(env: NodeJS.ProcessEnv = process.env): string | undefined {
  const bin = env[CLAUDE_BIN_OVERRIDE_ENV];
  if (!bin) return undefined;
  return `CẢNH BÁO: claude binary đang bị override qua ${CLAUDE_BIN_OVERRIDE_ENV}="${bin}" (chỉ dùng cho test/E2E) — KHÔNG phải claude thật.`;
}

/**
 * `deps.spawn` for production `run`: passes every non-"claude" spawn through
 * untouched, and for "claude" substitutes `AVATAR_AUTOPILOT_CLAUDE_BIN` when
 * set — same interception shape as
 * `test/helpers/spawn-fake-claude-worker.ts`'s `makeFakeClaudeWorkerSpawn`,
 * just env-driven instead of test-fixture-path-driven so the identical
 * production code path is what E2E exercises (no parallel "test-only spawn
 * builder" to drift from prod).
 */
export function buildProductionSpawn(env: NodeJS.ProcessEnv = process.env): SpawnFn {
  return ((command: string, args: readonly string[], options: unknown) => {
    const bin = command === "claude" ? resolveClaudeBin(env) : command;
    // @ts-expect-error -- SpawnFn's overloads don't line up with a generic
    // passthrough signature; runtime behavior matches node:child_process.spawn
    // (same cast shape as test/helpers/spawn-fake-claude-worker.ts).
    return nodeSpawn(bin, args, options);
  }) as unknown as SpawnFn;
}

export interface BuildRunCommandSessionDepsOptions {
  controlRoot: string;
  supervisorStartedAt: string | undefined;
  env?: NodeJS.ProcessEnv;
}

export interface BuildRunCommandSessionDepsResult {
  sessionDeps: SessionRunnerDeps;
  currentWorker: () => { pid: number; pgid: number } | undefined;
}

/**
 * Real `SessionRunnerDeps` for `avatar autopilot run`. No injected binding
 * writer override — production always uses the default
 * `writeAutopilotBindingFile` (session-runner.ts's own default). Phase 05:
 * wraps the production spawn so a live `claude` worker's pid/pgid is
 * recorded to `<controlRoot>/active-worker.json` — the evidence `avatar
 * autopilot unlock` needs to kill an orphaned worker group after a hard
 * SIGKILL of the supervisor.
 */
export function buildRunCommandSessionDeps(
  opts: BuildRunCommandSessionDepsOptions,
): BuildRunCommandSessionDepsResult {
  const rec = wrapSpawnRecordingActiveWorker({
    spawn: buildProductionSpawn(opts.env ?? process.env),
    controlRoot: opts.controlRoot,
    supervisorStartedAt: opts.supervisorStartedAt,
  });
  return {
    sessionDeps: {
      spawn: rec.spawn,
      now: () => Date.now(),
      sleep: (ms: number) => new Promise((resolve) => setTimeout(resolve, ms)),
    },
    currentWorker: rec.current,
  };
}
